/**
 * Server-Sent Events proxy for ludicrous-gateway
 * Handles streaming responses from providers
 */

import type { Response } from "express";
import type { StreamHandler } from "../types/providers.js";
import { getLogger, type Logger } from "../utils/logger.js";

/**
 * SSE writer configuration
 */
export interface SSEWriterConfig {
  /** Keep-alive interval in milliseconds */
  keepAliveIntervalMs?: number;
  /** Whether to send ping events */
  enablePing?: boolean;
}

/**
 * SSE writer for sending events to client
 */
export class SSEWriter implements StreamHandler {
  private response: Response;
  private closed: boolean = false;
  private keepAliveInterval: NodeJS.Timeout | null = null;
  private logger: Logger;

  constructor(response: Response, config: SSEWriterConfig = {}) {
    this.response = response;
    this.logger = getLogger().child({ component: "sse-writer" });

    // Set SSE headers
    response.setHeader("Content-Type", "text/event-stream");
    response.setHeader("Cache-Control", "no-cache");
    response.setHeader("Connection", "keep-alive");
    response.setHeader("X-Accel-Buffering", "no"); // Disable nginx buffering

    // Start keep-alive if enabled
    if (config.enablePing && config.keepAliveIntervalMs) {
      this.startKeepAlive(config.keepAliveIntervalMs);
    }

    // Handle client disconnect
    response.on("close", () => {
      this.close();
    });
  }

  /**
   * Send an SSE event to the client
   */
  onEvent(event: string, data: string): void {
    if (this.closed) {
      return;
    }

    try {
      if (event && event !== "data") {
        this.response.write(`event: ${event}\n`);
      }
      this.response.write(`data: ${data}\n\n`);
    } catch (error) {
      this.logger.error("Error writing SSE event", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      this.close();
    }
  }

  /**
   * Send a raw SSE line
   */
  sendRaw(line: string): void {
    if (this.closed) {
      return;
    }

    try {
      this.response.write(line);
    } catch (error) {
      this.logger.error("Error writing raw SSE", {
        error: error instanceof Error ? error.message : "Unknown error",
      });
      this.close();
    }
  }

  /**
   * Called when stream completes successfully
   */
  onComplete(): void {
    this.close();
  }

  /**
   * Called when stream errors
   */
  onError(error: Error): void {
    if (this.closed) {
      return;
    }

    this.logger.error("Stream error", { error: error.message });

    // Send error event to client
    this.onEvent(
      "error",
      JSON.stringify({
        type: "error",
        error: {
          type: "stream_error",
          message: error.message,
        },
      })
    );

    this.close();
  }

  /**
   * Send a ping event
   */
  sendPing(): void {
    if (this.closed) {
      return;
    }

    this.onEvent("ping", JSON.stringify({ type: "ping" }));
  }

  /**
   * Check if the writer is closed
   */
  isClosed(): boolean {
    return this.closed;
  }

  /**
   * Close the SSE connection
   */
  close(): void {
    if (this.closed) {
      return;
    }

    this.closed = true;

    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }

    try {
      this.response.end();
    } catch {
      // Ignore errors on close
    }
  }

  /**
   * Start keep-alive pings
   */
  private startKeepAlive(intervalMs: number): void {
    this.keepAliveInterval = setInterval(() => {
      this.sendPing();
    }, intervalMs);
  }
}

/**
 * Create an SSE writer for a response
 */
export function createSSEWriter(
  response: Response,
  config?: SSEWriterConfig
): SSEWriter {
  return new SSEWriter(response, config);
}

/**
 * SSE proxy that forwards events from a provider to a client
 */
export class SSEProxy {
  private writer: SSEWriter;
  private logger: Logger;

  constructor(response: Response) {
    this.writer = new SSEWriter(response, {
      enablePing: true,
      keepAliveIntervalMs: 15000,
    });
    this.logger = getLogger().child({ component: "sse-proxy" });
  }

  /**
   * Get the stream handler for provider
   */
  getHandler(): StreamHandler {
    return this.writer;
  }

  /**
   * Forward an event from provider to client
   */
  forward(event: string, data: string): void {
    this.writer.onEvent(event, data);
  }

  /**
   * Signal completion
   */
  complete(): void {
    this.writer.onComplete();
  }

  /**
   * Signal error
   */
  error(err: Error): void {
    this.writer.onError(err);
  }

  /**
   * Check if proxy is still active
   */
  isActive(): boolean {
    return !this.writer.isClosed();
  }

  /**
   * Force close the proxy
   */
  close(): void {
    this.writer.close();
  }
}

/**
 * Create an SSE proxy for a response
 */
export function createSSEProxy(response: Response): SSEProxy {
  return new SSEProxy(response);
}
